import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useMarketing, useMarketingStats, useKundenMarketing, generatePostText } from '@/hooks/useMarketing';
import type { SocialMediaPost, PostTemplate } from '@/hooks/useMarketing';
import {
  BarChart3, Plus, Image, Send, Download, Copy, Calendar, GripVertical,
  Instagram, Facebook, Eye, Trash2, Edit, Star, Gift, ExternalLink,
  TrendingUp, Users, FileImage, Megaphone, CheckCircle2, Clock, Loader2,
  Hash, Sparkles, ArrowRight
} from 'lucide-react';

// ── Pipeline Stages ──
const PIPELINE_STAGES = [
  { id: 'entwurf', label: 'Entwurf', color: 'bg-slate-100 text-slate-700', icon: Edit },
  { id: 'bereit', label: 'Bereit', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
  { id: 'geplant', label: 'Geplant', color: 'bg-amber-100 text-amber-700', icon: Calendar },
  { id: 'veroeffentlicht', label: 'Veröffentlicht', color: 'bg-emerald-100 text-emerald-700', icon: Send },
];

// ── Post-Text Templates für automatische Generierung ──
const POST_VORLAGEN: Record<string, string> = {
  schadensfall: '{{marke}} {{modell}} — Schadenhöhe: {{schadenhoehe}} € — Wiederbeschaffungswert: {{wbw}} € — Professionelle Begutachtung durch SV Büro Koop. Wir holen das Maximum für unsere Kunden raus.',
  vorher_nachher: 'Vorher ➡️ Nachher — {{marke}} {{modell}} — Professionelle Schadensdokumentation durch SV Büro Koop 📸',
  tipp: '💡 Tipp: {{titel}} — {{text}}',
  team: 'Unser Team bei der Arbeit 🔧 — Professionelle Begutachtung mit modernster Technik — SV Büro Koop, Lüdenscheid',
  kundenstimme: '⭐⭐⭐⭐⭐ — Wieder ein zufriedener Kunde! — {{marke}} {{modell}} — Schnelle und professionelle Abwicklung.',
};

const DEFAULT_HASHTAGS = ['#Gutachter', '#SVBüroKoop', '#KfzGutachten', '#Lüdenscheid', '#Unfallschaden', '#Sachverständiger'];

// ═══════════════════════════════════════════════════════════
// ── Übersicht Tab ──
// ═══════════════════════════════════════════════════════════
function UebersichtTab() {
  const { stats, loading } = useMarketingStats();
  const { posts } = useMarketing();

  const recentPosts = useMemo(() => posts.slice(0, 5), [posts]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6">
      {/* KPI Karten */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-blue-50"><FileImage className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.postsGesamt}</p>
                <p className="text-xs text-muted-foreground">Posts gesamt</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-emerald-50"><Send className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.postsVeroeffentlicht}</p>
                <p className="text-xs text-muted-foreground">Veröffentlicht</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-purple-50"><Instagram className="w-5 h-5 text-purple-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.kundenMitEinwilligung}</p>
                <p className="text-xs text-muted-foreground">IG-Einwilligungen</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-amber-50"><Star className="w-5 h-5 text-amber-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.bewertungenBestaetigt}</p>
                <p className="text-xs text-muted-foreground">Bewertungen</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Zweite Reihe */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-slate-50"><Edit className="w-5 h-5 text-slate-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.postsEntwurf}</p>
                <p className="text-xs text-muted-foreground">Entwürfe</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-blue-50"><Clock className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.postsBereit + stats.postsGeplant}</p>
                <p className="text-xs text-muted-foreground">Bereit / Geplant</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-amber-50"><Megaphone className="w-5 h-5 text-amber-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.bewertungenAngefragt}</p>
                <p className="text-xs text-muted-foreground">Bew. angefragt</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-emerald-50"><Gift className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.tankgutscheineVergeben}</p>
                <p className="text-xs text-muted-foreground">Tankgutscheine</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Letzte Posts */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Letzte Posts</CardTitle>
        </CardHeader>
        <CardContent>
          {recentPosts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Noch keine Posts erstellt. Erstelle deinen ersten Social Media Post!</p>
          ) : (
            <div className="space-y-3">
              {recentPosts.map(post => (
                <div key={post.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent/30 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {post.plattform === 'instagram' ? <Instagram className="w-5 h-5" /> : <Facebook className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{post.titel}</p>
                    <p className="text-xs text-muted-foreground">{post.fahrzeug_marke} {post.fahrzeug_modell} · {new Date(post.created_at).toLocaleDateString('de-DE')}</p>
                  </div>
                  <Badge variant="outline" className={PIPELINE_STAGES.find(s => s.id === post.status)?.color || ''}>
                    {PIPELINE_STAGES.find(s => s.id === post.status)?.label || post.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── Post-Generator Tab ──
// ═══════════════════════════════════════════════════════════
function PostGeneratorTab() {
  const { templates, createPost } = useMarketing();
  const [showEditor, setShowEditor] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<PostTemplate | null>(null);
  const [akten, setAkten] = useState<any[]>([]);
  const [selectedAkte, setSelectedAkte] = useState<any | null>(null);
  const [postText, setPostText] = useState('');
  const [postTitel, setPostTitel] = useState('');
  const [hashtags, setHashtags] = useState<string[]>(DEFAULT_HASHTAGS);
  const [plattform, setPlattform] = useState('instagram');
  const [saving, setSaving] = useState(false);

  // Akten mit Instagram-Einwilligung laden
  useEffect(() => {
    supabase.from('akten')
      .select('id, akten_nr, fahrzeug_marke, fahrzeug_modell, fahrzeug_kennzeichen, kennzeichen, wiederbeschaffungswert, reparaturkosten_brutto, schadenart, kunde_name, status')
      .order('erstellt_am', { ascending: false })
      .limit(50)
      .then(({ data }) => { if (data) setAkten(data); });
  }, []);

  const handleSelectTemplate = (tmpl: PostTemplate) => {
    setSelectedTemplate(tmpl);
    if (selectedAkte) {
      const generated = generatePostText(tmpl, selectedAkte);
      setPostText(generated.text);
      setHashtags(generated.hashtags);
    } else {
      setPostText(tmpl.text_vorlage);
      setHashtags(tmpl.hashtag_vorlage);
    }
    setShowEditor(true);
  };

  const handleSelectAkte = (akteId: string) => {
    const akte = akten.find(a => a.id === akteId);
    setSelectedAkte(akte);
    if (akte) {
      setPostTitel(`${akte.fahrzeug_marke || ''} ${akte.fahrzeug_modell || ''} — ${akte.akten_nr}`.trim());
      if (selectedTemplate) {
        const generated = generatePostText(selectedTemplate, akte);
        setPostText(generated.text);
        setHashtags(generated.hashtags);
      }
    }
  };

  const handleSave = async (status: string = 'entwurf') => {
    setSaving(true);
    const { error } = await createPost({
      titel: postTitel || 'Neuer Post',
      post_text: postText,
      plattform,
      template_typ: selectedTemplate?.typ || 'schadensfall',
      status,
      akte_id: selectedAkte?.id || null,
      fahrzeug_marke: selectedAkte?.fahrzeug_marke || null,
      fahrzeug_modell: selectedAkte?.fahrzeug_modell || null,
      schadenhoehe: selectedAkte?.reparaturkosten_brutto || null,
      wiederbeschaffungswert: selectedAkte?.wiederbeschaffungswert || null,
      schadenart: selectedAkte?.schadenart || null,
      hashtags,
      kennzeichen_unkenntlich: true,
      instagram_einwilligung: true,
    });
    setSaving(false);
    if (!error) {
      toast.success(`Post als "${PIPELINE_STAGES.find(s => s.id === status)?.label}" gespeichert`);
      setShowEditor(false);
      setPostText('');
      setPostTitel('');
      setSelectedAkte(null);
      setSelectedTemplate(null);
    } else {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleCopyText = () => {
    const fullText = `${postText}\n\n${hashtags.join(' ')}`;
    navigator.clipboard.writeText(fullText);
    toast.success('Post-Text in Zwischenablage kopiert');
  };

  return (
    <div className="space-y-6">
      {/* Template Auswahl */}
      <div>
        <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Post-Template wählen</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {templates.map(tmpl => (
            <Card
              key={tmpl.id}
              className={`cursor-pointer transition-all hover:shadow-md hover:border-primary/40 ${selectedTemplate?.id === tmpl.id ? 'border-primary ring-2 ring-primary/20' : ''}`}
              onClick={() => handleSelectTemplate(tmpl)}
            >
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                    <Image className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm font-medium">{tmpl.name}</span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">{tmpl.beschreibung}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Post Editor */}
      {showEditor && (
        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Post erstellen — {selectedTemplate?.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Linke Spalte: Eingaben */}
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Akte auswählen</label>
                  <Select value={selectedAkte?.id || ''} onValueChange={handleSelectAkte}>
                    <SelectTrigger><SelectValue placeholder="Akte wählen (optional)" /></SelectTrigger>
                    <SelectContent>
                      {akten.map(a => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.akten_nr} — {a.fahrzeug_marke} {a.fahrzeug_modell} ({a.kennzeichen || a.fahrzeug_kennzeichen || '—'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Titel</label>
                  <Input value={postTitel} onChange={e => setPostTitel(e.target.value)} placeholder="Post-Titel" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Plattform</label>
                  <Select value={plattform} onValueChange={setPlattform}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="beide">Instagram + Facebook</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Post-Text</label>
                  <Textarea value={postText} onChange={e => setPostText(e.target.value)} rows={6} className="resize-none" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Hashtags</label>
                  <Input
                    value={hashtags.join(' ')}
                    onChange={e => setHashtags(e.target.value.split(' ').filter(Boolean))}
                    placeholder="#Gutachter #SVBüroKoop"
                  />
                </div>
              </div>

              {/* Rechte Spalte: Vorschau */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Vorschau</label>
                <div className="border rounded-xl overflow-hidden bg-white">
                  {/* Instagram-Style Header */}
                  <div className="flex items-center gap-2 p-3 border-b">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1a365d] to-[#c9a84c] flex items-center justify-center text-white text-xs font-bold">SK</div>
                    <div>
                      <p className="text-xs font-semibold">sv_buero_koop</p>
                      <p className="text-[10px] text-muted-foreground">SV Büro Koop · Lüdenscheid</p>
                    </div>
                  </div>
                  {/* Bild-Platzhalter */}
                  <div className="aspect-square bg-gradient-to-br from-slate-800 to-slate-600 flex items-center justify-center relative">
                    <div className="text-center text-white/80 p-6">
                      <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm font-medium">{selectedAkte?.fahrzeug_marke} {selectedAkte?.fahrzeug_modell}</p>
                      {selectedAkte?.reparaturkosten_brutto && (
                        <p className="text-lg font-bold mt-1">{Number(selectedAkte.reparaturkosten_brutto).toLocaleString('de-DE')} €</p>
                      )}
                      <p className="text-xs mt-2 opacity-60">Foto hier einfügen</p>
                    </div>
                    {/* Branding Overlay */}
                    <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm rounded-lg px-2.5 py-1.5 flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full bg-gradient-to-br from-[#1a365d] to-[#c9a84c]" />
                      <span className="text-[10px] font-bold text-slate-800">SV Büro Koop</span>
                    </div>
                  </div>
                  {/* Post Text */}
                  <div className="p-3">
                    <p className="text-xs leading-relaxed whitespace-pre-wrap">{postText}</p>
                    <p className="text-xs text-blue-500 mt-2">{hashtags.join(' ')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Aktions-Buttons */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <Button onClick={() => handleSave('entwurf')} variant="outline" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Edit className="w-4 h-4 mr-1" />}
                Als Entwurf speichern
              </Button>
              <Button onClick={() => handleSave('bereit')} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Bereit zum Posten
              </Button>
              <div className="flex-1" />
              <Button variant="ghost" size="sm" onClick={handleCopyText}>
                <Copy className="w-4 h-4 mr-1" /> Text kopieren
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowEditor(false)}>
                Abbrechen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hinweis wenn kein Template gewählt */}
      {!showEditor && (
        <Card className="border-dashed">
          <CardContent className="py-10 text-center">
            <Sparkles className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Wähle ein Template oben um einen neuen Post zu erstellen</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Oder erstelle einen Post direkt aus einer Akte heraus</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── Content-Pipeline Tab (Kanban) ──
// ═══════════════════════════════════════════════════════════
function ContentPipelineTab() {
  const { posts, movePost, deletePost } = useMarketing();
  const [draggedPost, setDraggedPost] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);

  const postsByStage = useMemo(() => {
    const grouped: Record<string, SocialMediaPost[]> = {};
    PIPELINE_STAGES.forEach(s => { grouped[s.id] = []; });
    posts.forEach(p => {
      if (grouped[p.status]) grouped[p.status].push(p);
      else grouped['entwurf']?.push(p);
    });
    return grouped;
  }, [posts]);

  const handleDragStart = (postId: string) => setDraggedPost(postId);
  const handleDragOver = (e: React.DragEvent, stageId: string) => { e.preventDefault(); setDragOverStage(stageId); };
  const handleDragLeave = () => setDragOverStage(null);
  const handleDrop = async (stageId: string) => {
    if (draggedPost) {
      await movePost(draggedPost, stageId);
      toast.success(`Post nach "${PIPELINE_STAGES.find(s => s.id === stageId)?.label}" verschoben`);
    }
    setDraggedPost(null);
    setDragOverStage(null);
  };

  const handleDelete = async (id: string) => {
    await deletePost(id);
    toast.success('Post gelöscht');
  };

  return (
    <div className="flex flex-col md:flex-row gap-4 overflow-x-hidden md:overflow-x-auto pb-4 md:pb-4" style={{ minHeight: '500px' }}>
      {PIPELINE_STAGES.map(stage => {
        const stagePosts = postsByStage[stage.id] || [];
        const StageIcon = stage.icon;
        return (
          <div
            key={stage.id}
            className={`flex-shrink-0 w-full md:w-72 rounded-xl border transition-all ${dragOverStage === stage.id ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'bg-muted/30'}`}
            onDragOver={e => handleDragOver(e, stage.id)}
            onDragLeave={handleDragLeave}
            onDrop={() => handleDrop(stage.id)}
          >
            {/* Stage Header */}
            <div className="p-3 border-b">
              <div className="flex items-center gap-2">
                <StageIcon className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-semibold">{stage.label}</span>
                <Badge variant="secondary" className="ml-auto text-xs">{stagePosts.length}</Badge>
              </div>
            </div>

            {/* Cards */}
            <div className="p-2 space-y-2 min-h-[200px]">
              {stagePosts.map(post => (
                <div
                  key={post.id}
                  draggable
                  onDragStart={() => handleDragStart(post.id)}
                  className={`bg-background rounded-lg border p-3 cursor-grab active:cursor-grabbing transition-all hover:shadow-sm ${draggedPost === post.id ? 'opacity-40 scale-95' : ''}`}
                >
                  <div className="flex items-start gap-2">
                    <GripVertical className="w-4 h-4 text-muted-foreground/40 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{post.titel}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {post.fahrzeug_marke} {post.fahrzeug_modell}
                      </p>
                      <div className="flex items-center gap-1.5 mt-2">
                        {post.plattform === 'instagram' || post.plattform === 'beide' ? (
                          <Instagram className="w-3 h-3 text-purple-500" />
                        ) : null}
                        {post.plattform === 'facebook' || post.plattform === 'beide' ? (
                          <Facebook className="w-3 h-3 text-blue-600" />
                        ) : null}
                        <span className="text-[10px] text-muted-foreground ml-auto">
                          {new Date(post.created_at).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                      {post.geplant_fuer && (
                        <div className="flex items-center gap-1 mt-1.5">
                          <Calendar className="w-3 h-3 text-amber-500" />
                          <span className="text-[10px] text-amber-600">
                            Geplant: {new Date(post.geplant_fuer).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Quick Actions */}
                  <div className="flex items-center gap-1 mt-2 pt-2 border-t">
                    <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs" onClick={handleCopyPostText.bind(null, post)}>
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-destructive" onClick={() => handleDelete(post.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
              {stagePosts.length === 0 && (
                <div className="flex items-center justify-center h-24 text-xs text-muted-foreground/50 border border-dashed rounded-lg">
                  Keine Posts
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function handleCopyPostText(post: SocialMediaPost) {
  const fullText = `${post.post_text}\n\n${post.hashtags.join(' ')}`;
  navigator.clipboard.writeText(fullText);
  toast.success('Post-Text kopiert');
}

// ═══════════════════════════════════════════════════════════
// ── Statistik Tab ──
// ═══════════════════════════════════════════════════════════
function StatistikTab() {
  const { stats, loading } = useMarketingStats();
  const { kunden, updateKunde } = useKundenMarketing();

  const kundenMitEinwilligung = useMemo(() => kunden.filter(k => k.instagram_einwilligung), [kunden]);
  const kundenMitBewertung = useMemo(() => kunden.filter(k => k.bewertung_bestaetigt), [kunden]);

  const handleTankgutschein = async (kundeId: string) => {
    await updateKunde(kundeId, { tankgutschein_vergeben: true, tankgutschein_datum: new Date().toISOString() } as any);
    toast.success('Tankgutschein als vergeben markiert');
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6">
      {/* Post-Statistik */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Post-Statistik
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {PIPELINE_STAGES.map(stage => {
              const count = stage.id === 'entwurf' ? stats.postsEntwurf
                : stage.id === 'bereit' ? stats.postsBereit
                : stage.id === 'geplant' ? stats.postsGeplant
                : stats.postsVeroeffentlicht;
              const pct = stats.postsGesamt > 0 ? Math.round((count / stats.postsGesamt) * 100) : 0;
              return (
                <div key={stage.id} className="text-center">
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-xs text-muted-foreground">{stage.label}</div>
                  <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Instagram-Einwilligungen */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Instagram className="w-4 h-4 text-purple-600" /> Instagram-Einwilligungen ({kundenMitEinwilligung.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {kundenMitEinwilligung.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Noch keine Kunden mit Instagram-Einwilligung</p>
          ) : (
            <div className="space-y-2">
              {kundenMitEinwilligung.map(k => (
                <div key={k.id} className="flex items-center gap-3 p-3 rounded-lg border">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                    {k.vorname[0]}{k.nachname[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{k.vorname} {k.nachname}</p>
                    {k.instagram_handle && (
                      <p className="text-xs text-purple-600">@{k.instagram_handle.replace('@', '')}</p>
                    )}
                  </div>
                  {k.tankgutschein_vergeben ? (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 text-xs">
                      <Gift className="w-3 h-3 mr-1" /> Gutschein vergeben
                    </Badge>
                  ) : (
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => handleTankgutschein(k.id)}>
                      <Gift className="w-3 h-3 mr-1" /> Tankgutschein vergeben
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Google Bewertungen */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" /> Google Bewertungen ({kundenMitBewertung.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {kundenMitBewertung.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Noch keine bestätigten Bewertungen</p>
          ) : (
            <div className="space-y-2">
              {kundenMitBewertung.map(k => (
                <div key={k.id} className="flex items-center gap-3 p-3 rounded-lg border">
                  <Star className="w-5 h-5 text-amber-500 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{k.vorname} {k.nachname}</p>
                    <p className="text-xs text-muted-foreground">{k.email}</p>
                  </div>
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 text-xs">Bewertet</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── Bewertung & Instagram Onboarding Steps ──
// ═══════════════════════════════════════════════════════════
export function BewertungStep({ kundeId, onNext }: { kundeId?: string; onNext: () => void }) {
  const [bestaetigt, setBestaetigt] = useState(false);

  const handleBewertung = async () => {
    // Google Maps Bewertungsseite öffnen
    window.open('https://search.google.com/local/writereview?placeid=ChIJX8YxXXXXXXXXXX', '_blank');
    if (kundeId) {
      await supabase.from('kunden').update({ bewertung_angefragt: true }).eq('id', kundeId);
    }
  };

  const handleWeiter = async () => {
    if (kundeId && bestaetigt) {
      await supabase.from('kunden').update({ bewertung_bestaetigt: true }).eq('id', kundeId);
    }
    onNext();
  };

  return (
    <div className="max-w-md mx-auto text-center space-y-6 py-8">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-50 flex items-center justify-center">
        <Star className="w-8 h-8 text-amber-500" />
      </div>
      <div>
        <h2 className="text-xl font-bold mb-2">Waren Sie zufrieden?</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Hinterlassen Sie uns eine Google-Bewertung — das dauert nur 1 Minute und hilft uns sehr!
        </p>
      </div>
      <Button onClick={handleBewertung} className="w-full" size="lg">
        <ExternalLink className="w-4 h-4 mr-2" />
        Jetzt auf Google bewerten
      </Button>
      <label className="flex items-center gap-2 justify-center cursor-pointer">
        <input type="checkbox" checked={bestaetigt} onChange={e => setBestaetigt(e.target.checked)} className="rounded" />
        <span className="text-sm text-muted-foreground">Ich habe eine Bewertung hinterlassen</span>
      </label>
      <Button variant="ghost" onClick={handleWeiter} className="w-full">
        Weiter <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
}

export function InstagramEinwilligungStep({ kundeId, onNext }: { kundeId?: string; onNext: () => void }) {
  const [einwilligung, setEinwilligung] = useState<boolean | null>(null);
  const [handle, setHandle] = useState('');

  const handleWeiter = async () => {
    if (kundeId) {
      await supabase.from('kunden').update({
        instagram_einwilligung: einwilligung === true,
        instagram_handle: einwilligung ? handle || null : null,
      }).eq('id', kundeId);
    }
    onNext();
  };

  return (
    <div className="max-w-md mx-auto text-center space-y-6 py-8">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
        <Instagram className="w-8 h-8 text-purple-600" />
      </div>
      <div>
        <h2 className="text-xl font-bold mb-2">Darf Ihr Fahrzeug ins Rampenlicht?</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Wir würden gerne ein Foto Ihres Fahrzeugs (ohne personenbezogene Daten, Kennzeichen unkenntlich) auf unserem Instagram-Kanal teilen.
          Als Dankeschön erhalten Sie bei jedem Repost einen <strong>20 € Tankgutschein</strong>!
        </p>
      </div>

      <div className="space-y-3">
        <Button
          onClick={() => setEinwilligung(true)}
          variant={einwilligung === true ? 'default' : 'outline'}
          className="w-full"
          size="lg"
        >
          <CheckCircle2 className="w-4 h-4 mr-2" />
          Ja, ich stimme zu
        </Button>
        <Button
          onClick={() => setEinwilligung(false)}
          variant={einwilligung === false ? 'default' : 'outline'}
          className="w-full"
          size="lg"
        >
          Nein, danke
        </Button>
      </div>

      {einwilligung === true && (
        <div className="text-left">
          <label className="text-xs font-medium text-muted-foreground mb-1 block">Instagram-Handle (optional)</label>
          <Input
            value={handle}
            onChange={e => setHandle(e.target.value)}
            placeholder="@ihr_username"
            className="text-center"
          />
          <p className="text-[10px] text-muted-foreground mt-1">Damit wir Sie beim Repost markieren können</p>
        </div>
      )}

      <p className="text-[10px] text-muted-foreground">
        DSGVO-Hinweis: Es werden keine personenbezogenen Daten veröffentlicht. Das Kennzeichen wird unkenntlich gemacht.
      </p>

      {einwilligung !== null && (
        <Button onClick={handleWeiter} className="w-full">
          Weiter <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── Hauptkomponente ──
// ═══════════════════════════════════════════════════════════
export default function MarketingPage() {
  const [, navigate] = useLocation();
  const [activeTab, setActiveTab] = useState('uebersicht');

  // URL-basiertes Tab-Routing
  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes('/marketing/posts')) setActiveTab('posts');
    else if (path.includes('/marketing/pipeline')) setActiveTab('pipeline');
    else if (path.includes('/marketing/statistik')) setActiveTab('statistik');
    else setActiveTab('uebersicht');
  }, []);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'uebersicht') navigate('/marketing');
    else navigate(`/marketing/${tab}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Marketing & Social Media</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Content erstellen, planen und veröffentlichen</p>
        </div>
        <Button onClick={() => { setActiveTab('posts'); navigate('/marketing/posts'); }}>
          <Plus className="w-4 h-4 mr-1" /> Neuer Post
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 max-w-lg h-auto gap-2">
          <TabsTrigger value="uebersicht" className="text-xs min-h-[44px]">
            <TrendingUp className="w-3.5 h-3.5 mr-1" /> Übersicht
          </TabsTrigger>
          <TabsTrigger value="posts" className="text-xs min-h-[44px]">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Post-Generator
          </TabsTrigger>
          <TabsTrigger value="pipeline" className="text-xs min-h-[44px]">
            <ArrowRight className="w-3.5 h-3.5 mr-1" /> Pipeline
          </TabsTrigger>
          <TabsTrigger value="statistik" className="text-xs min-h-[44px]">
            <BarChart3 className="w-3.5 h-3.5 mr-1" /> Statistik
          </TabsTrigger>
        </TabsList>

        <TabsContent value="uebersicht"><UebersichtTab /></TabsContent>
        <TabsContent value="posts"><PostGeneratorTab /></TabsContent>
        <TabsContent value="pipeline"><ContentPipelineTab /></TabsContent>
        <TabsContent value="statistik"><StatistikTab /></TabsContent>
      </Tabs>
    </div>
  );
}
